import type { PayloadAction, SerializedError } from '@reduxjs/toolkit'
import { isFluxStandardAction, isRejectedWithValue, Middleware } from '@reduxjs/toolkit'
import { FetchBaseQueryError, FetchBaseQueryMeta as RTKQuery_FetchBaseQueryMeta, SubscriptionOptions } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'

/**
 * Endpoints that should report all errors to Sentry, regardless of status code.
 */
const ALWAYS_REPORT_ENDPOINTS = new Set(['uploadFile'])

/**
 * Determines if an error status represents a reportable client error.
 * We only report HTTP 4xx client errors, excluding authorization errors (401, 403).
 * Server errors (5xx) and non-HTTP errors (FETCH_ERROR, TIMEOUT_ERROR, PARSING_ERROR, CUSTOM_ERROR)
 * are not reported.
 */
const isReportableClientError = (status: FetchBaseQueryError['status']): boolean => {
  if (typeof status !== 'number') {
    return false
  }
  const isClientError = status >= 400 && status < 500
  const isAuthorizationError = status === 401 || status === 403
  return isClientError && !isAuthorizationError
}

type FetchBaseQueryArgs<OriginalArgs = unknown> = {
  type: 'query' | 'mutation'
  subscribe: boolean
  forceRefetch: boolean
  subscriptionOptions: SubscriptionOptions
  endpointName: string
  originalArgs: OriginalArgs
  queryCacheKey: string
}

type FetchBaseQueryMeta = {
  aborted: boolean
  arg: FetchBaseQueryArgs
  baseQueryMeta: RTKQuery_FetchBaseQueryMeta
  condition: boolean
  rejectedWithValue: boolean
  requestId: string
}

/**
 * Log and report errors from rejected RTK Query actions to Sentry.
 * - For endpoints in ALWAYS_REPORT_ENDPOINTS: all errors are reported
 * - For other endpoints: only client errors (4xx, excluding 401/403) are reported
 */
export const sentryErrorMiddleware: Middleware = () => (next) => (action) => {
  if (isFluxStandardAction(action) && isRejectedWithValue(action)) {
    const payloadAction = action as unknown as PayloadAction<FetchBaseQueryError, string, FetchBaseQueryMeta, (typeof action)['error']>
    const { meta, payload, error } = payloadAction
    const endpointName = meta?.arg?.endpointName ?? 'unknown'

    const shouldAlwaysReport = ALWAYS_REPORT_ENDPOINTS.has(endpointName)
    if (!shouldAlwaysReport && !isReportableClientError(payload.status)) {
      return next(action)
    }

    const url = meta?.baseQueryMeta?.request?.url
    const statusTagKey = typeof payload.status === 'number' ? 'http_response_status_code' : 'error_type'

    Sentry.withScope((scope) => {
      scope.setTag('endpoint_name', endpointName)
      scope.setTag(statusTagKey, payload.status)
      const fingerprint = ['rtk-query', endpointName, payload.status?.toString()].filter((value) => value != null)
      scope.setFingerprint(fingerprint)
      scope.setContext('rtk_query', {
        endpointName,
        payload,
        error,
        queryCacheKey: meta?.arg?.queryCacheKey,
        requestId: meta?.requestId,
        type: meta?.arg?.type,
        subscribe: meta?.arg?.subscribe,
        forceRefetch: meta?.arg?.forceRefetch,
      })
      scope.setExtra('request_url', url)

      Sentry.captureException(buildSentryError(endpointName, payload.status, error))
    })
  }
  return next(action)
}

const buildSentryError = (endpointName: string, status: FetchBaseQueryError['status'], actionError?: SerializedError) => {
  const message = `API request failed for "${endpointName}" with ${status}`
  const error = new Error(message)

  if (actionError?.name) {
    error.name = actionError.name
  }

  if (actionError?.stack) {
    error.stack = actionError.stack
  }

  return error
}
